import { useState } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";

import { Heading, Loader } from "@stellar/design-system";

import { SectionHeader } from "@/components/SectionHeader";

import { Routes } from "@/constants/settings";

import { useCoSigningStatus } from "@/apiQueries/multisigEnrollment";

import { deriveCoSigningView } from "@/helpers/coSigningView";

import { useRedux } from "@/hooks/useRedux";

import { CoSignerIntro } from "./components/CoSignerIntro";
import { CoSignerWizard } from "./components/CoSignerWizard";
import { InviteeOwnerFlow } from "./components/InviteeOwnerFlow";

import "./index.scss";

type OwnerView = "intro" | "wizard";

export const MultisigSetup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editRequested = searchParams.get("edit") === "1";

  const { profile, organization } = useRedux("profile", "organization");
  const coSigningStatus = useCoSigningStatus({
    userId: profile.data.id,
    role: profile.data.role ?? null,
  });

  const orgName = organization.data.name || "your organization";
  const currentUserId = profile.data.id;
  const pendingInviteeId = coSigningStatus.data?.org.pending_signer?.user_id ?? "";
  const activeSession = coSigningStatus.data?.enrollment ?? null;
  const goToSettings =
    (type: "tm" | "g" = "g") =>
    () =>
      navigate(`${Routes.SETTINGS}?tab=${type}`);
  const view = deriveCoSigningView(coSigningStatus.data);

  const [ownerView, setOwnerView] = useState<OwnerView>(view === "INITIALIZE" ? "intro" : "wizard");

  const header = (
    <SectionHeader>
      <SectionHeader.Row>
        <SectionHeader.Content>
          <Heading as="h2" size="sm">
            Co-signing setup
          </Heading>
        </SectionHeader.Content>
      </SectionHeader.Row>
    </SectionHeader>
  );

  if (coSigningStatus.isLoading) {
    return (
      <div className="MultisigSetup">
        {header}
        <div className="MultisigConnect__pending">
          <Loader size="1rem" />
          <span>Loading…</span>
        </div>
      </div>
    );
  }

  // MY_TURN caller — pick the invitee flow that matches their role.
  if (view === "MY_TURN") {
    return (
      <div className="MultisigSetup">
        {header}
        <InviteeOwnerFlow
          orgName={orgName}
          currentUserId={currentUserId}
          activeSession={activeSession}
          onBack={goToSettings()}
          onDone={goToSettings()}
          onGoToTeamMembers={goToSettings("tm")}
        />
      </div>
    );
  }

  if (editRequested) {
    return (
      <div className="MultisigSetup">
        {header}
        <CoSignerWizard
          orgName={orgName}
          currentUserId={currentUserId}
          pendingInviteeId={pendingInviteeId}
          activeSession={activeSession}
          onBack={goToSettings()}
          onDone={goToSettings()}
          onGoToTeamMembers={goToSettings("tm")}
          editMode
        />
      </div>
    );
  }

  // Fresh owner-1 flow: intro → full 3-step wizard.
  return (
    <div className="MultisigSetup">
      {header}
      {ownerView === "intro" && (
        <CoSignerIntro onStart={() => setOwnerView("wizard")} onCancel={goToSettings("tm")} />
      )}
      {ownerView === "wizard" && (
        <CoSignerWizard
          orgName={orgName}
          currentUserId={currentUserId}
          pendingInviteeId={pendingInviteeId}
          activeSession={activeSession}
          onBack={() => setOwnerView("intro")}
          onDone={goToSettings()}
          onGoToTeamMembers={goToSettings("tm")}
        />
      )}
    </div>
  );
};
